import { useEffect, useRef, useState } from "react";
import useEvent from "react-use-event-hook";
import { useRecoilValue } from "recoil";

import {
  appLanguageSelector,
  companyIdSelector,
  Log,
  MapPlacesApi,
  type ICoordinate,
} from "@ryde/shared";

import { MAPLIBRE_PLACE_INFO_STATUS } from "./maplibre-place-info.constants";
import type { IMapLibrePlaceInfoLookup } from "./maplibre-place-info.models";
import { toPlaceInfoLookup } from "./maplibre-place-info.utils";

/**
 * Looks up one clicked point at a time. A newer click, a close or an unmount bumps the generation and
 * aborts the request in flight, so an older answer can never land in the popup of a newer point.
 */
export const useMapLibrePlaceInfoLookup = () => {
  const companyId = useRecoilValue(companyIdSelector);
  const languageCode = useRecoilValue(appLanguageSelector);
  const [lookup, setLookup] = useState<IMapLibrePlaceInfoLookup>();
  const generationRef = useRef(0);
  const abortControllerRef = useRef<AbortController | undefined>(undefined);
  // useEvent callbacks read primitives through a container so they always see the current values.
  const session = { companyId, languageCode };

  const cancelPending = useEvent((): void => {
    generationRef.current += 1;
    abortControllerRef.current?.abort();
    abortControllerRef.current = undefined;
  });

  const close = useEvent((): void => {
    cancelPending();
    setLookup(undefined);
  });

  const lookUp = useEvent(async (coordinate: ICoordinate): Promise<void> => {
    cancelPending();

    // No session is not a failed lookup: there is nothing to ask or retry, so no popup opens at all.
    if (!session.companyId) {
      Log.warn("Place info lookup aborted: companyId is not loaded yet");
      setLookup(undefined);
      return;
    }

    const generation = generationRef.current;

    setLookup({ coordinate, status: MAPLIBRE_PLACE_INFO_STATUS.Loading });

    const abortController = new AbortController();
    abortControllerRef.current = abortController;

    const result = await MapPlacesApi.findByCoordinate({
      companyId: session.companyId,
      coordinate,
      languageCode: session.languageCode,
      abortController,
    }).catch((error: unknown) => {
      // A superseded request is aborted on purpose; only the live one failing is worth a warning.
      if (generation === generationRef.current) {
        Log.warn("Place info lookup failed", error);
      }

      return undefined;
    });

    if (generation !== generationRef.current) {
      return;
    }

    abortControllerRef.current = undefined;
    setLookup(toPlaceInfoLookup({ coordinate, result }));
  });

  const retry = useEvent((): void => {
    if (lookup) {
      void lookUp(lookup.coordinate);
    }
  });

  useEffect(() => cancelPending, [cancelPending]);

  return { lookup, lookUp, retry, close };
};
